/**
 * Inserts server markdown via TipTap Markdown manager, one top-level doc block at a time,
 * so the edit surface feels streamed instead of pasted in one fragment.
 */

function jitter(ms) {
  return ms + Math.floor(Math.random() * 26);
}

function estimateWeight(blockJson) {
  try {
    return Math.min(220, Math.floor(JSON.stringify(blockJson).length / 6));
  } catch {
    return 48;
  }
}

/**
 * @param {import("@tiptap/core").Editor} editor
 * @param {string} markdown
 * @param {number} startPos Document position captured before the async round-trip (cursor).
 * @param {{ signal?: AbortSignal; baseDelayMs?: number }} [opts]
 */
export async function insertMarkdownWithTypingReveal(editor, markdown, startPos, opts = {}) {
  const { signal, baseDelayMs = 46 } = opts;

  const maxPos = editor.state.doc.content.size;
  let pos =
    typeof startPos === "number" && Number.isFinite(startPos)
      ? Math.min(Math.max(0, Math.floor(startPos)), maxPos)
      : editor.state.selection.from;

  const md = typeof markdown === "string" ? markdown.trim() : "";

  const fallbackMarkdownInsert = () => {
    editor
      .chain()
      .focus()
      .insertContentAt(pos, md, { contentType: "markdown", updateSelection: true })
      .scrollIntoView()
      .run();
  };

  if (!md || !editor?.markdown?.parse) {
    fallbackMarkdownInsert();
    return;
  }

  let parsed;
  try {
    parsed = editor.markdown.parse(md);
  } catch (e) {
    console.warn("neoAiMarkdownInsert: parse failed", e);
    fallbackMarkdownInsert();
    return;
  }

  const blocks = Array.isArray(parsed?.content) ? parsed.content : [];
  if (!blocks.length) {
    fallbackMarkdownInsert();
    return;
  }

  editor.view.focus();

  for (let i = 0; i < blocks.length; i++) {
    if (signal?.aborted) return;

    await new Promise((r) => setTimeout(r, jitter(baseDelayMs + estimateWeight(blocks[i]) * 0.15)));

    if (signal?.aborted) return;

    const blockJson = blocks[i];

    try {
      const node = editor.state.schema.nodeFromJSON(blockJson);
      let tr = editor.state.tr.insert(pos, node);

      editor.view.dispatch(tr.scrollIntoView());
      editor.view.focus();

      pos += node.nodeSize;
    } catch (e) {
      console.warn("neoAiMarkdownInsert: block insert failed, falling back to full markdown", e);
      editor
        .chain()
        .insertContentAt(pos, md, { contentType: "markdown", updateSelection: true })
        .scrollIntoView()
        .run();
      return;
    }
  }

  editor.chain().focus().setTextSelection(pos).scrollIntoView().run();
}
